<template>
    <div>
        <h2
            v-t="'Gantner-Zutrittsmedien'"
            class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
        ></h2>

        <table class="kgr-table kgr-table--striped table-groups kgr-row kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th
                        v-t="'Name'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'ID'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="accessMedium in accessMediums"
                    :key="accessMedium.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(accessMedium)">
                            <kgr-text-input
                                v-model="editAccessMediums.find(medium => medium.id === accessMedium.id).name"
                                :error="error.messageForField('name')"
                            />
                        </template>
                        <template v-else>
                            {{ accessMedium.name }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        {{ accessMedium.identifier }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <kgr-icon-edit
                            v-if="accessMedium.isEditable"
                            :size="15"
                            :disabled="isBeingEdited(accessMedium)"
                            class="kgr-table__action"
                            @click="edit(accessMedium)"
                        />
                        <kgr-delete-dialog
                            :value="confirmDeleteId === accessMedium.id"
                            :object-name="accessMedium.name"
                            @post-input="onDeleteDialogInput"
                            @confirm="remove(accessMedium)"
                        >
                            <kgr-icon-delete
                                v-if="accessMedium.isDeletable"
                                :title="$t('Zutrittsmedium löschen')"
                                @click="showDeleteDialog(accessMedium)"
                            />
                        </kgr-delete-dialog>
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="isCreatable"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pending"
            @click="update()"
        />

        <kgr-button
            v-if="isCreatable"
            class="button_add_job"
            :value="$t('Zutrittsmedium hinzufügen')"
            :pending="pendingJob"
            @click="addAssignmentJob()"
        />

        <kgr-lightbox
            v-model="showLightbox"
            :headline="$t('Zutrittsmedium hinzufügen')"
            animated
            @post-input="closeAndAbort()"
        >
            <kgr-assignment-job
                v-if="assignmentJob !== null"
                :assignment-job="assignmentJob"
                :member-id="memberId"
                @close="closeAndReload()"
            />
        </kgr-lightbox>
    </div>
</template>

<script>
import AssignmentJobDialog from './AssignmentJobDialog';
import ClubMemberAccessMediumAssignmentJobRepository from '../../../js/repository/ClubMemberAccessMediumAssignmentJobRepository';
import ClubMemberAccessMediumGantnerRepository from '../../../js/repository/ClubMemberAccessMediumGantnerRepository';
import DeleteDialog from '../../common/modal/DeleteDialog';
import Error from '../../../js/model/Error';
import IconDelete from '../../common/icon/IconDelete';
import IconEdit from '../../common/icon/IconEdit';
import KgrButton from '../../common/form/button/KgrButton';
import Lightbox from '../../common/modal/Lightbox';
import TextInput from '../../common/form/input/TextInput';

export default {
    components: {
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-text-input': TextInput,
        'kgr-delete-dialog': DeleteDialog,
        'kgr-lightbox': Lightbox,
        'kgr-assignment-job': AssignmentJobDialog,
    },
    props: {
        memberId: {
            type: Number,
            required: true,
        },
        accessMediums: {
            type: Array,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            editAccessMediums: [],
            pending: false,
            pendingJob: false,
            confirmDeleteId: null,
            assignmentJob: null,
            showLightbox: false,
            error: new Error(),
            jobDeleted: false,
        }
    },
    methods: {
        isBeingEdited(accessMedium) {
            return this.getEditIndex(accessMedium) !== -1;
        },
        getEditIndex(accessMedium) {
            return this.editAccessMediums.map(editAccessMedium => editAccessMedium.id).indexOf(accessMedium.id);
        },
        edit(accessMedium) {
            if (this.isBeingEdited(accessMedium)) {
                return;
            }
            this.editAccessMediums.push(accessMedium);
        },
        remove(accessMedium) {
            ClubMemberAccessMediumGantnerRepository.delete(accessMedium.id)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        isSavable() {
            return this.editAccessMediums.length > 0;
        },
        update() {
            if (this.editAccessMediums.length === 0) {
                return;
            }
            this.pending = true;
            ClubMemberAccessMediumGantnerRepository.update(this.editAccessMediums)
                .then(() => {
                    this.editAccessMediums = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                    this.error = new Error();
                })
                .catch(error => {
                    this.$notify.error('saveChanges.error');
                    this.error = error;
                })
                .finally(() => this.pending = false);
        },
        onDeleteDialogInput() {
            this.confirmDeleteId = null;
        },
        showDeleteDialog(accessMedium) {
            this.confirmDeleteId = accessMedium.id;
        },
        addAssignmentJob() {
            this.pendingJob = true;
            this.jobDeleted = false;
            ClubMemberAccessMediumAssignmentJobRepository.add(this.memberId)
                .then(() => this.pollAssignmentJob())
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                    this.pendingJob = false;
                });
        },
        pollAssignmentJob() {
            if (this.pendingJob) {
                setTimeout(() => {
                    this.loadAssignmentJob()
                    this.pollAssignmentJob();
                }, 3000)
            }
        },
        loadAssignmentJob() {
            ClubMemberAccessMediumAssignmentJobRepository.find(this.memberId)
                .then(assignmentJob => {
                    this.assignmentJob = assignmentJob;
                    this.showLightbox = new Date() <= this.assignmentJob.validUntil && !this.jobDeleted;
                    if (this.assignmentJob.id === undefined || !this.showLightbox) {
                        this.closeAndReload();
                    }
                });
        },
        closeAndAbort() {
            this.showLightbox = false;
            this.assignmentJob = null;
            this.pendingJob = false;
            this.jobDeleted = true;
            ClubMemberAccessMediumAssignmentJobRepository.delete(this.memberId);
        },
        closeAndReload() {
            this.showLightbox = false;
            this.assignmentJob = null;
            this.pendingJob = false;
            this.emitUpdated();
        },
        emitUpdated() {
            this.$emit('updated');
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_add_job {
        margin-left: 50px;
    }
</style>
